import './Buffer.scss';

import { useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';

import bem from '../../../../../../../../../utils/bem';
import type { LineItemEntity, MultipleEditDrawerLineItemsFormValues } from '../../../../../types';
import { useBufferContext } from './BufferContext';
import type { BufferTableRef } from './BufferTable/BufferTable';
import BufferTable from './BufferTable/BufferTable';
import { AUTOMATED_BUFFER_TITLE, USER_APPLIED_BUFFER_TITLE } from './constants';

type BufferProps = Readonly<{
  isExpanded: boolean;
}>;

const [block, element] = bem('multiple-edit-buffer');

export default function Buffer({ isExpanded }: BufferProps): JSX.Element {
  const {
    values: { entities = [] },
  } = useFormikContext<MultipleEditDrawerLineItemsFormValues>();

  const { registerApplyFunction, unregisterApplyFunction } = useBufferContext();
  const automatedTableRef = useRef<BufferTableRef>(null);
  const userAppliedTableRef = useRef<BufferTableRef>(null);

  const automatedBufferLineItems = entities.filter((li: LineItemEntity) => li.automateBuffer === true);

  const userAppliedBufferLineItems = entities.filter((li: LineItemEntity) => !li.automateBuffer);

  useEffect(() => {
    const applyFunction = (): void => {
      automatedTableRef.current?.applyChangesToFormik();
      userAppliedTableRef.current?.applyChangesToFormik();
    };

    registerApplyFunction(applyFunction);

    return () => {
      unregisterApplyFunction(applyFunction);
    };
  }, [registerApplyFunction, unregisterApplyFunction]);

  return (
    <div className={block()}>
      <div className={element('header')}></div>
      {automatedBufferLineItems.length > 0 && (
        <div className={element('section')}>
          <div className={element('title')}>{AUTOMATED_BUFFER_TITLE}</div>
          <BufferTable
            ref={automatedTableRef}
            isExpanded={isExpanded}
            entities={automatedBufferLineItems}
            isAutomated
          />
        </div>
      )}

      {userAppliedBufferLineItems.length > 0 && (
        <div className={element('section')}>
          <div className={element('title')}>{USER_APPLIED_BUFFER_TITLE}</div>
          <BufferTable ref={userAppliedTableRef} isExpanded={isExpanded} entities={userAppliedBufferLineItems} />
        </div>
      )}
    </div>
  );
}
